import type { AgentKey, Attention, AttentionKind } from './types';
import { KIND_RANK } from './types';

/**
 * The status vocabulary (spec 17 §6.4). **This replaces free text in the
 * attention cell**, and it is closed on purpose.
 *
 * The kind picks the colour and icon; **the code picks the words.** The front end
 * never composes a sentence, and an unrecognised code renders nothing at all —
 * not the raw code, not a fallback. A made-up status is worse than a blank one.
 */
export interface AttentionCode {
  code: string;
  agent: AgentKey;
  kind: AttentionKind;
  /** `%n` is replaced with the count. Nothing else is interpolated. */
  text: string;
  /** Where the fix actually lives — not always the agent's own page. */
  targetLabel: string;
}

export const ATTENTION_CODES: AttentionCode[] = [
  // AI Receptionist — 5 codes, plus clear.
  {
    code: 'no_number_routed',
    agent: 'receptionist',
    kind: 'blocked',
    text: 'No phone number routed to ELVA',
    targetLabel: 'Settings › Phone numbers',
  },
  {
    code: 'sms_registration_failed',
    agent: 'receptionist',
    kind: 'blocked',
    text: 'Carrier registration failed — texting is off',
    targetLabel: 'Settings › Phone numbers',
  },
  {
    code: 'triage_unclaimed',
    agent: 'receptionist',
    kind: 'stalled',
    text: '%n urgent triage nobody has opened',
    targetLabel: 'Triage Inbox',
  },
  {
    code: 'transfer_unanswered',
    agent: 'receptionist',
    kind: 'stalled',
    text: '%n warm transfers rang out',
    targetLabel: 'Warm transfer',
  },
  /**
   * The conflict between §6.4 and the `DashStates` sheet is settled (P348): **§6.4
   * was right, the sheet was stale**, and `needed_a_human` is deregistered.
   *
   * P344 made this column a **state**. `needed_a_human` was a resolved count over
   * the period — precisely what that decision removed — and the *Needed a human*
   * tile already carries it. Two facts, one column, and only the present-tense one
   * belongs here.
   */
  {
    code: 'waiting_for_a_human',
    agent: 'receptionist',
    kind: 'handoff',
    text: '%n waiting for a human right now',
    targetLabel: 'Triage Inbox',
  },

  // Appointment Reminders — 4 codes, plus clear.
  {
    code: 'no_channel_enabled',
    agent: 'reminders',
    kind: 'blocked',
    text: 'Every channel is switched off',
    targetLabel: 'Settings › Reminders',
  },
  {
    code: 'unconfirmed_inside_24h',
    agent: 'reminders',
    kind: 'stalled',
    text: '%n unconfirmed, and the visit is inside 24 hours',
    targetLabel: 'Calendar',
  },
  {
    code: 'sequence_broke',
    agent: 'reminders',
    kind: 'stalled',
    text: '%n stopped part-way through',
    targetLabel: 'Communication log',
  },
  {
    code: 'post_op_for_a_human',
    agent: 'reminders',
    kind: 'handoff',
    text: '%n post-op replies for a person to read',
    targetLabel: 'Triage Inbox',
  },

  // Recall — 4 codes, plus clear. The clock lives between the last two.
  {
    code: 'no_audience',
    agent: 'recall',
    kind: 'blocked',
    text: 'No audience has been built',
    targetLabel: 'Settings › Recall',
  },
  {
    code: 'never_attempted',
    agent: 'recall',
    kind: 'stalled',
    text: '%n are due and have never been contacted',
    targetLabel: 'Recall',
  },
  /**
   * `call_rung_overdue` is deregistered (amendment 20 §5). It fired at a hardcoded
   * 7 days — but a human-call task **expires at the Recall setting's value and the
   * patient moves on**, so a day-8 stall can never occur. The fixture was
   * demonstrating a state the product cannot reach.
   *
   * Both codes below read `recall.human_call.deadline_open_days`. **Nothing here
   * hardcodes a number of days.**
   */
  {
    code: 'human_calls_expiring',
    agent: 'recall',
    kind: 'stalled',
    text: '%n calls expire today — nobody has made them',
    targetLabel: 'Triage Inbox',
  },
  {
    code: 'awaiting_human_call',
    agent: 'recall',
    kind: 'handoff',
    text: '%n waiting for a person to call',
    targetLabel: 'Recall',
  },

  // Gap Filler — 3 codes, plus clear.
  {
    code: 'no_rules',
    agent: 'gap_filler',
    kind: 'blocked',
    text: 'No gap rules have been set',
    targetLabel: 'Settings › Gap Filler',
  },
  {
    code: 'unreachable_gaps',
    agent: 'gap_filler',
    kind: 'stalled',
    text: "%n gaps it can't offer to anyone",
    // Links to Appointment reasons, NOT to Gap Filler — that is where the fix
    // lives (§6.5). The routing chain is the rule: a gap is fillable only if some
    // appointment reason maps to a role whose provider is free.
    targetLabel: 'Settings › Appointment reasons',
  },
  {
    code: 'offers_unanswered',
    agent: 'gap_filler',
    kind: 'handoff',
    text: '%n offers out, no reply yet',
    targetLabel: 'Gap Filler',
  },

  // Treatment Coordinator — 4 codes, plus clear.
  {
    code: 'no_plans_synced',
    agent: 'treatment',
    kind: 'blocked',
    text: 'No treatment plans have synced',
    targetLabel: 'Settings › Practice management',
  },
  {
    code: 'step_has_no_content',
    agent: 'treatment',
    kind: 'stalled',
    text: '%n steps have nothing to send',
    targetLabel: 'Settings › TC',
  },
  {
    code: 'over_threshold_overdue',
    agent: 'treatment',
    kind: 'stalled',
    text: '%n over $1,000 waiting more than 3 days',
    targetLabel: 'Treatment Intelligence',
  },
  {
    code: 'held_for_human',
    agent: 'treatment',
    kind: 'handoff',
    text: '%n held for a person — over $1,000',
    targetLabel: 'Treatment Intelligence',
  },
  // Staging fix 29 §6: TC has no money gate — the right-column handoff is simply
  // the people who need a person (a call-me tap or a question on their plan).
  {
    code: 'tc_needs_person',
    agent: 'treatment',
    kind: 'handoff',
    text: '1 to call · 1 to answer',
    targetLabel: 'Calls',
  },

  // Invisalign Coordinator — 3 codes, plus clear.
  {
    code: 'no_sequence',
    agent: 'invisalign',
    kind: 'blocked',
    text: 'No sequence has been set',
    targetLabel: 'Settings › Invisalign',
  },
  {
    code: 'said_yes_never_booked',
    agent: 'invisalign',
    kind: 'stalled',
    text: '%n said yes and never booked',
    targetLabel: 'Treatment Intelligence',
  },
  {
    code: 'awaiting_reply',
    agent: 'invisalign',
    kind: 'handoff',
    text: '%n waiting on a reply',
    targetLabel: 'Treatment Intelligence',
  },
  {
    // Amendment 30 §8: the right column reads "7 ready for a nudge" → the Call list.
    code: 'ready_for_nudge',
    agent: 'invisalign',
    kind: 'stalled',
    text: '%n ready for a nudge',
    targetLabel: 'Call list',
  },

  // Accounts receivable — 3 codes, plus clear. `held_for_human` is deliberately
  // the same code on two agents, because it is the same threshold — A/R's field,
  // read by the TC (P308). The agent disambiguates the wording.
  {
    code: 'no_payment_link',
    agent: 'ar',
    kind: 'blocked',
    text: 'No payment link connected',
    targetLabel: 'Settings › A/R',
  },
  {
    code: 'links_expired_unpaid',
    agent: 'ar',
    kind: 'stalled',
    text: '%n payment links expired unpaid',
    targetLabel: 'Collections',
  },
  {
    code: 'held_for_human',
    agent: 'ar',
    kind: 'handoff',
    text: '%n over $1,000 held for a person',
    targetLabel: 'Collections',
  },
  // Amendment 28 §5: the working A/R row's handoff — replies, disputes and
  // plan questions a person picks up, from the closed list.
  {
    code: 'ar_needs_person',
    agent: 'ar',
    kind: 'handoff',
    text: '8 to call · 3 to answer',
    targetLabel: 'Calls',
  },
];

export const CLEAR_TEXT = 'Nothing waiting';
export const CLEAR_CODE = 'clear';

const AGENT_KEYS: AgentKey[] = [
  'receptionist',
  'sms',
  'email',
  'reminders',
  'recall',
  'gap_filler',
  'treatment',
  'invisalign',
  'ar',
];

/**
 * `clear` is a **row in the table for every one of the seven agents** (P349), not
 * a sentence beneath it and not a special case in the lookup.
 *
 * The original spec listed the codes in a table and then said *"every agent also
 * has `clear`"* as prose underneath — and **a code documented as prose under a
 * table is a code that does not get registered.** That is exactly what happened:
 * the Invisalign row, the only agent with nothing waiting, hit the unknown-code
 * path and rendered blank where the board draws a green *Nothing waiting*.
 *
 * The rule to carry forward: **`clear` is a code the back end sends. It is never
 * inferred from the absence of the others.** An agent with nothing waiting is
 * making a positive statement, and it renders green.
 */
const CLEAR_CODES: AttentionCode[] = AGENT_KEYS.map((agent) => ({
  code: CLEAR_CODE,
  agent,
  kind: 'clear' as const,
  text: CLEAR_TEXT,
  targetLabel: CLEAR_TEXT,
}));

/** The complete vocabulary: 26 codes plus `clear` on each of the seven agents. */
export const VOCABULARY: AttentionCode[] = [...ATTENTION_CODES, ...CLEAR_CODES];

/**
 * Look a code up **for its agent** — `held_for_human` means something slightly
 * different on the TC and on A/R, and the row should say the right one.
 *
 * Returns `undefined` for anything not in the vocabulary, which the renderer
 * treats as *render nothing*.
 */
export function lookupCode(agent: AgentKey, code: string): AttentionCode | undefined {
  return VOCABULARY.find((c) => c.agent === agent && c.code === code);
}

/** `%n` is the only substitution. Nothing else is composed. */
export function attentionText(entry: AttentionCode, count: number): string {
  return entry.text.replace('%n', count.toLocaleString('en-US'));
}

/**
 * Precedence (P342): highest kind wins; a tie inside a kind goes to **the one
 * whose `since` is oldest**, then `+N more` after the text.
 *
 * **Two kinds never appear in the same row.** If an agent is blocked its stalls
 * are real but irrelevant — nothing is moving anyway, and listing them competes
 * with the one thing somebody should go and fix.
 */
export function resolveAttention(
  agent: AgentKey,
  items: Attention[],
): { chosen: Attention; entry: AttentionCode; moreInKind: number } | undefined {
  // **Absence of an attention object is an error state, not a calm one** (§7.1):
  // it means the agent did not report. A calm agent sends `clear`, which is why
  // `clear` is a code rather than something inferred from an empty array.
  if (items.length === 0 && typeof console !== 'undefined') {
    console.error(`[dashboard] agent "${agent}" reported no attention object — that is not the same as clear`);
  }
  // Anything outside the vocabulary is dropped here rather than rendered blank
  // further down, so it also cannot win precedence over something real.
  const known = items.filter((a) => lookupCode(agent, a.code) !== undefined);
  if (known.length === 0) return undefined;

  const topRank = Math.max(...known.map((a) => KIND_RANK[a.kind]));
  const inKind = known.filter((a) => KIND_RANK[a.kind] === topRank);
  const sorted = [...inKind].sort((a, b) => Date.parse(a.since) - Date.parse(b.since));
  const chosen = sorted[0];
  if (chosen === undefined) return undefined;
  const entry = lookupCode(agent, chosen.code);
  if (entry === undefined) return undefined;
  return { chosen, entry, moreInKind: inKind.length - 1 };
}

/** Tone for each kind. The kind picks the colour and the icon, always (§6.1). */
export const KIND_STYLE: Record<AttentionKind, { fg: string; bg: string; icon: 'alert' | 'circle' }> = {
  blocked: { fg: '#BE4440', bg: '#FBEDEC', icon: 'alert' },
  stalled: { fg: '#8A6415', bg: '#FBF3E4', icon: 'alert' },
  handoff: { fg: '#565E72', bg: '#F1F3F9', icon: 'circle' },
  clear: { fg: '#1F7A56', bg: '#EEF6F2', icon: 'circle' },
};
